import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { domainCheck } from '~/api/auth';
import AuthenticationStore from '~/auth/authenticationStore';
import { ssoDebugLog } from '~/auth/ssoDebugLog';
import { useAuthenticationStore } from '~/auth/useAuthenticationStore';
import { LoginViewType } from '~/enums/LoginViewType';
import { QueryParamType } from '~/enums/QueryParamType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { enterClientSlug } from '~/routing/clientSlug';
import { useRouting } from '~/routing/useRouting';
import {
  getClientFromUrl,
  getRawClientFromUrl,
  isAtWorkspaceScopedPath,
  isLocalhostShell,
  isPreview,
} from '~/routing/utils';
import { getAbsoluteRedirectUrl } from '~/services/redirects';

import type { LoginQuery } from './types';

import { LoginPage } from './LoginPage';
import {
  getPopupModeInfo,
  handlePopupLoginSuccess,
  handlePopupLoginSuccessWhenReady,
} from './popupLoginUtils';
import { useAegisLoginProps } from './useAegisLoginProps';
import { useRolodexLoginProps } from './useRolodexLoginProps';

interface SsoConfig {
  enabled: boolean;
  error: string | null;
  provider_name: string;
  signon_url: string;
}

interface DomainCheckResponse {
  sso: SsoConfig;
  oidc: { enabled: boolean; error: string | null };
  enableRolodex: boolean;
}

const AS = AuthenticationStore();

function resolveLoginRedirectPath({
  stage,
  returnTo,
  shouldUseSearchAsHomePage,
}: {
  stage: LoginViewType;
  returnTo: string | null;
  shouldUseSearchAsHomePage: boolean;
}) {
  if (stage === LoginViewType.LoggedInWithWelcome) {
    return shouldUseSearchAsHomePage
      ? '/search?welcomeMessage=true'
      : '/?welcomeMessage=true';
  }
  return returnTo || '/';
}

function Page() {
  const intl = useIntl();
  const { location, navigate } = useRouting();
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);

  // A workspace already in the URL is a picker the visitor never has to see, so
  // open straight on the credentials stage. Starting at `Domain` and correcting
  // once the domain check lands paints the picker for a frame — and that frame
  // also spends the form's one-shot autofocus on the wrong input. The check
  // still runs underneath to fill in SSO configuration.
  const [stage, setStage] = useState<LoginViewType>(() =>
    getClientFromUrl() ? LoginViewType.Email : LoginViewType.Domain,
  );
  const [error, setError] = useState<string | null | undefined>();
  const [returnTo, setRedirectTo] = useState<string | null>(null);

  const [ssoConfigs, setSsoConfigs] = useState<SsoConfig | null>(null);
  const [workspace, setWorkspace] = useState<string>('');

  const [enableOidc, setEnableOidc] = useState<boolean>(false);
  const [enableRolodex, setEnableRolodex] = useState<boolean>(false);

  const { isLoggedIn } = useAuthenticationStore();
  // Captured once at mount so the redirect effect can distinguish "user was
  // already logged in before any form interaction" (no stage guard needed)
  // from "user just completed the login form" (must wait for stage to settle).
  const wasLoggedInOnMount = useRef(isLoggedIn);
  const isEmbed = AS.isEmbed();

  // Check if we're in popup mode (e.g., from Word add-in using Office Dialog API)
  // Intentionally captured once at mount - even if URL changes during auth flows,
  // the popup context remains the same for the entire session
  const [isPopupMode] = useState<boolean>(() => getPopupModeInfo().isPopupMode);

  // enable auto-login if in embedded login mode and ssoConfigs exists or if rolodex enabled for oidc
  const isSsoOnly = (isEmbed && ssoConfigs) || (enableRolodex && enableOidc);

  // When the user arrives via an OAuth authorization URL (e.g. from academy.evisort.com),
  // App.tsx saves the OAuth params (redirect_uri, client_id, state) to sessionStorage
  // before the user logs in. After login, we must skip the hard redirect here so that
  // App.tsx's checkRedirectToOAuth can call the backend /ext/oauth/redirect endpoint
  // and redirect to the OAuth callback with an authorization code.
  const isPendingAegisOAuth = Boolean(AS.getAegisOAuthProxyVars()[0]);
  // When rolodex's /oauth/authorize redirects here with ?return_url=...,
  // AuthSessionBootstrap persists the URL in sessionStorage. When the user
  // logs in, AuthSessionBootstrap's checkRolodexOAuthBounce navigates back to
  // the authorize endpoint before this component's effect can interfere.
  const isPendingRolodexOAuth = Boolean(AS.getRolodexOAuthBounceUrl());
  const isPendingOAuth = isPendingAegisOAuth || isPendingRolodexOAuth;

  ssoDebugLog('Login page render', {
    stage,
    isPendingAegisOAuth,
    aegisOAuthVars: AS.getAegisOAuthProxyVars(),
    isPendingRolodexOAuth,
    rolodexOAuthBounceUrl: AS.getRolodexOAuthBounceUrl(),
    isPopupMode,
    workspace,
    returnTo,
    pathname: globalThis.location.pathname,
    search: globalThis.location.search,
  });

  const shouldUseSearchAsHomePage = useFlag(FlagType.SearchAsHomePage);

  const checkClient = (ws: string) => {
    const fromLocation = location.state?.from;
    const formData = fromLocation
      ? {
          client: ws,
          return_to: getAbsoluteRedirectUrl(
            window.location.origin,
            ws.toLowerCase(),
            fromLocation,
          ),
        }
      : { client: ws };

    setIsCheckingDomain(true);
    return domainCheck(formData)
      .then((raw: unknown) => {
        const response = raw as DomainCheckResponse;
        if (response) {
          // PR #5695 made PrivateRoute reactive to the auth store via
          // useAuthenticationStore(). When the Stytch/Rolodex login completes,
          // notifyAuthChange() and setStage(LoggedIn) fire in the same React
          // batch, causing PrivateRoute to unmount the Page component before it
          // can execute window.location.href = '/garfield/'. To avoid this, we
          // redirect to the workspace-prefixed /login URL here (which is served
          // as a public route and is not wrapped in PrivateRoute), so Page is
          // never unmounted during login.
          if (!isAtWorkspaceScopedPath(window.location.pathname, ws)) {
            const slug = ws.toLowerCase();
            const returnToParam = fromLocation
              ? `?returnTo=${encodeURIComponent(`${fromLocation.pathname}${fromLocation.search ?? ''}`)}`
              : '';
            enterClientSlug(slug, `/${slug}/login${returnToParam}`);
            return;
          }
          if (!location.search.includes(QueryParamType.SamlError)) {
            setStage(LoginViewType.Email);
          }
          if (response.sso.error) {
            setError(
              intl.formatMessage(
                {
                  id: 'LoginPage.invalidSsoConfiguration',
                  defaultMessage: 'Invalid SSO Configuration: {error}',
                  description:
                    'Error message for invalid SSO configuration. Example: error = "Missing SAML endpoint"',
                },
                { error: response.sso.error },
              ),
            );
          } else if (response.oidc.error) {
            setError(
              intl.formatMessage(
                {
                  id: 'LoginPage.invalidOidcConfiguration',
                  defaultMessage: 'Invalid SSO Configuration: {error}',
                  description:
                    'Error message for invalid OIDC configuration. Example: error = "Invalid client ID"',
                },
                { error: response.oidc.error },
              ),
            );
          } else {
            setError(null);
          }

          if (response.sso.enabled) {
            setSsoConfigs(response.sso);
          }

          setEnableRolodex(response.enableRolodex);
          setEnableOidc(response.oidc.enabled);
        }
        setIsCheckingDomain(false);
      })
      .catch(() => {
        setError(
          intl.formatMessage({
            id: 'LoginPage.genericError',
            defaultMessage: 'An error occurred. Please check again.',
            description: 'Generic error message when domain check fails',
          }),
        );
      })
      .finally(() => {
        setIsCheckingDomain(false);
      });
  };

  /**
   * Mapping of old/DEPRECATED LoginViewType to new LoginPage.Stage
   */
  const handleChangeWorkspace = (ws: string) => {
    setWorkspace(ws);
    checkClient(ws);
  };

  // `isFetching` drives the Continue button's own spinner. Surfacing the domain
  // check there — rather than swapping the page for a full-screen loader —
  // keeps the auth chrome mounted, so the workspace → sign-in hand-off no
  // longer flashes the bare white document between the two stages.
  const queryChangeWorkspace = [
    handleChangeWorkspace,
    {
      isFetching: isCheckingDomain,
      ...(ssoConfigs
        ? { error: ssoConfigs.error ?? undefined, isSuccess: true }
        : {}),
    },
  ] as unknown as LoginQuery<(workspace: string) => void>;

  useEffect(() => {
    const isLoggedIn = AS.isLoggedIn();
    const hasReturnTo = location && location.state && location.state.from;

    const subdomain = getClientFromUrl();

    // Handle popup mode for already-authenticated users BEFORE any redirect
    // This intercepts the normal "already logged in" redirect flow
    if (isLoggedIn && isPopupMode) {
      handlePopupLoginSuccessWhenReady();
      return; // Don't proceed with normal flow
    }

    // Parse returnTo before the isLoggedIn early-return so an already-logged-in
    // user landing on the login page with a deep-link target (e.g. via a bookmark
    // or the ?returnTo= param we encode in checkClient) is sent to their intended
    // destination rather than always to root.
    const searchParams = new URLSearchParams(location.search);
    const returnToUrl = searchParams.get('returnTo');

    if (isLoggedIn && !isEmbed) {
      navigate(returnToUrl ?? '/');
      return;
    }

    // When the user typed a workspace on a clientless /login URL, checkClient
    // did window.location.replace('/{ws}/login?returnTo=...') to preserve the
    // deep-link target across the browser-level navigation. Read it back here.
    // No need to use an extra decodeURIComponent: URLSearchParams.get
    // already percent-decodes once.
    if (returnToUrl) {
      setRedirectTo(returnToUrl);
    } else if (hasReturnTo) {
      const { pathname, search } = location.state!.from!;
      if (search) {
        setRedirectTo(`${pathname}/${search}`);
      } else {
        setRedirectTo(`${pathname}`);
      }
    }

    if (subdomain) handleChangeWorkspace(subdomain);
  }, [location.search, navigate, isPopupMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoggedIn || !workspace) return;

    const redirectWorkspace =
      isPreview || isLocalhostShell
        ? getRawClientFromUrl()
        : workspace.toLowerCase();

    if (isPendingOAuth) {
      // AuthSessionBootstrap's effect fires before this one (it's earlier in
      // the component tree). For Rolodex it already called removeRolodexOAuthBounce()
      // and set window.location.href = bounceUrl. For Aegis it fired the async
      // redirectToOAuth() call. In both cases, navigating to /{ws}/ here would
      // overwrite the OAuth redirect (Rolodex) or land at a page where the Aegis
      // proxy vars are still intact (Aegis — harmless but redundant).
      // Just return so AuthSessionBootstrap's navigation wins.
      ssoDebugLog(
        'Login page: OAuth pending, deferring to AuthSessionBootstrap',
        { isPendingOAuth },
      );
      return;
    }

    if (isPopupMode) {
      handlePopupLoginSuccess();
      return;
    }

    // When the user went through the login form (not already logged in on mount),
    // setLogIn() in the auth hooks sets stage via a setTimeout after
    // notifyAuthChange() fires. Guard until stage settles so we never redirect
    // before the welcome flag is known — covers both aegis (100ms) and rolodex (250ms).
    if (
      !wasLoggedInOnMount.current &&
      stage !== LoginViewType.LoggedIn &&
      stage !== LoginViewType.LoggedInWithWelcome
    ) {
      return;
    }

    const targetPath = resolveLoginRedirectPath({
      stage,
      returnTo,
      shouldUseSearchAsHomePage,
    });

    ssoDebugLog('Login page redirecting (no pending OAuth)', {
      redirectWorkspace,
      targetPath,
    });

    // `targetPath` is basename-relative, so when the URL already carries the
    // workspace the router can route to it directly; only a workspace the
    // basename doesn't match needs the slug swapped underneath it.
    if (redirectWorkspace === getRawClientFromUrl()) {
      navigate(targetPath, { replace: true });
      return;
    }

    enterClientSlug(redirectWorkspace, `/${redirectWorkspace}${targetPath}`);
  }, [
    isLoggedIn,
    navigate,
    workspace,
    stage,
    isPendingOAuth,
    isPopupMode,
    returnTo,
    shouldUseSearchAsHomePage,
    // wasLoggedInOnMount is a ref (stable value, set once at mount) — not needed in deps
  ]);

  // Move aegis related props to useAegisLoginProps and remove them at once
  // when migration to rolodex is done
  const aegisLoginPageProps = useAegisLoginProps({
    location,
    navigate,
    workspace,
    ssoConfigs,
    stage,
    setStage,
    error,
    setError,
  });

  const rolodexLoginPageProps = useRolodexLoginProps({
    location,
    navigate,
    workspace,
    enableOidc,
    setStage,
    isPopupMode,
  });

  if (isSsoOnly) return null;

  return (
    /* aegisLoginPageProps returns LoginQuery arrays with 2 elements; JS file returns inferred 2-tuple so we cast at boundary */
    <LoginPage
      {...(aegisLoginPageProps as any)}
      {...rolodexLoginPageProps}
      workspace={workspace}
      isSsoOnly={isSsoOnly}
      enableRolodex={enableRolodex}
      enableOidc={enableOidc}
      queryChangeWorkspace={queryChangeWorkspace}
    />
  );
}

export default Page;
